// ------------------------------------------------------------
// @project Pagali - Plataforma de Pagamentos
// @file Remessas.tsx
// @author Arlinda Peixoto
// @company Pagali
// @description Remessas Internacionais PagaliPay — monitorização, corredores, compliance
// @date 2026-04-22
// ------------------------------------------------------------

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
} from '@mui/material';
import {
  Public,
  Refresh,
  Warning,
  CheckCircle,
  AccessTime,
  TrendingUp,
  AttachMoney,
  Flag,
} from '@mui/icons-material';
import DataTable, { Column } from '../../components/Common/DataTable';
import { useTranslation } from 'react-i18next';
import bankingService from '../../services/banking.service';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface IntlTransfer extends Record<string, any> {
  id: number;
  reference: string;
  amount: number;
  fee: number;
  status: string;
  created_at: string;
  dest_country: string;
  dest_currency: string;
  dest_amount: number;
  exchange_rate: number;
  dest_bank_swift: string;
  dest_bank_name: string;
  dest_holder: string;
  purpose: string;
  client_name: string;
  client_email: string;
  kyc_level: string;
  compliance_flag: boolean;
}

interface CorridorStat {
  dest_country: string;
  dest_currency: string;
  total: number;
  volume_cve: number;
  volume_dest: number;
  fees_collected: number;
  avg_amount: number;
  completed: number;
  processing: number;
  failed: number;
}

interface ExchangeRate {
  buy: number;
  sell: number;
  updated: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  COMPLETED: { label: 'Concluído', color: 'success' },
  PROCESSING: { label: 'Em processamento', color: 'warning' },
  PENDING: { label: 'Pendente', color: 'info' },
  FAILED: { label: 'Falhado', color: 'error' },
  CANCELLED: { label: 'Cancelado', color: 'default' },
};

const COUNTRY_FLAGS: Record<string, string> = {
  PT: '🇵🇹', US: '🇺🇸', BR: '🇧🇷', FR: '🇫🇷', NL: '🇳🇱', CV: '🇨🇻',
};

function formatCVE(amount: number): string {
  return new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', minimumFractionDigits: 0 }).format(amount);
}

function CorridorProgress({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1, bgcolor: 'action.selected', borderRadius: 1, height: 6 }}>
        <Box sx={{ width: `${pct}%`, bgcolor: '#7c4dff', height: '100%', borderRadius: 1 }} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 30 }}>{Math.round(pct)}%</Typography>
    </Box>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function Remessas() {
  const { t } = useTranslation();

  const [tab, setTab] = useState(0);
  const [transfers, setTransfers] = useState<IntlTransfer[]>([]);
  const [corridorStats, setCorridorStats] = useState<CorridorStat[]>([]);
  const [exchangeRates, setExchangeRates] = useState<Record<string, ExchangeRate>>({});
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await bankingService.getInternationalTransfers({
        status: statusFilter || undefined,
        destCountry: countryFilter || undefined,
      });
      if (res?.success) {
        setTransfers(res.data?.transfers ?? []);
        setCorridorStats(res.data?.corridorStats ?? []);
        setExchangeRates(res.data?.exchangeRates ?? {});
        setStats(res.data?.stats ?? null);
      } else {
        setError(res?.error ?? 'Erro desconhecido');
      }
    } catch (e: any) {
      setError(e.message ?? 'Erro ao carregar remessas');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, countryFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const maxVolume = Math.max(...corridorStats.map(c => Number(c.volume_cve) || 0), 1);

  // ── Stats cards ─────────────────────────────────────────────────────────────

  const statCards = stats ? [
    { label: 'Total Remessas', value: stats.total ?? 0, icon: <Public />, color: '#7c4dff' },
    { label: 'Volume Total', value: formatCVE(stats.total_volume ?? 0), icon: <TrendingUp />, color: '#1565c0' },
    { label: 'Taxas Cobradas', value: formatCVE(stats.total_fees ?? 0), icon: <AttachMoney />, color: '#2e7d32' },
    { label: 'Em processamento', value: stats.processing ?? 0, icon: <AccessTime />, color: '#f57f17' },
    { label: 'Flags compliance', value: stats.compliance_flags ?? 0, icon: <Warning />, color: '#bf360c' },
  ] : [];

  // ── Colunas tabela ──────────────────────────────────────────────────────────

  const columns: Column<IntlTransfer>[] = [
    {
      id: 'reference',
      label: 'Referência',
      minWidth: 130,
      format: (v, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {row.compliance_flag && (
            <Tooltip title="Valor elevado — requer revisão de compliance">
              <Warning sx={{ fontSize: 16, color: 'error.main' }} />
            </Tooltip>
          )}
          <Typography variant="body2" fontFamily="monospace">{v}</Typography>
        </Box>
      ),
    },
    {
      id: 'client_name',
      label: 'Remetente',
      minWidth: 160,
      format: (v, row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{v}</Typography>
          <Typography variant="caption" color="text.secondary">{row.client_email}</Typography>
        </Box>
      ),
    },
    {
      id: 'dest_country',
      label: 'Destino',
      minWidth: 130,
      format: (v, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography>{COUNTRY_FLAGS[v as string] ?? '🌍'}</Typography>
          <Box>
            <Typography variant="body2" fontWeight={500}>{v}</Typography>
            <Typography variant="caption" color="text.secondary">{row.dest_bank_name}</Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'amount',
      label: 'Valor (CVE)',
      minWidth: 120,
      format: (v) => (
        <Typography variant="body2" fontWeight={600}>{formatCVE(v as number)}</Typography>
      ),
    },
    {
      id: 'dest_amount',
      label: 'Valor Destino',
      minWidth: 120,
      format: (v, row) => (
        <Typography variant="body2">
          {(v as number).toFixed(2)} {row.dest_currency}
        </Typography>
      ),
    },
    {
      id: 'exchange_rate',
      label: 'Câmbio',
      minWidth: 90,
      format: (v, row) => (
        <Typography variant="caption">{(v as number).toFixed(4)} CVE/{row.dest_currency}</Typography>
      ),
    },
    {
      id: 'kyc_level',
      label: 'KYC',
      minWidth: 100,
      format: (v) => (
        <Chip
          label={v as string}
          color={v === 'ENHANCED' ? 'success' : v === 'STANDARD' ? 'info' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      id: 'status',
      label: 'Estado',
      minWidth: 130,
      format: (v) => {
        const cfg = STATUS_CONFIG[v as string] ?? { label: v, color: 'default' };
        return <Chip label={cfg.label} color={cfg.color} size="small" />;
      },
    },
    {
      id: 'created_at',
      label: 'Data',
      minWidth: 130,
      format: (v) => (
        <Typography variant="caption">
          {new Date(v as string).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}
        </Typography>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Public sx={{ color: '#7c4dff', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Remessas Internacionais</Typography>
            <Typography variant="body2" color="text.secondary">
              PagaliPay — monitorização de transferências internacionais
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Atualizar">
          <IconButton onClick={fetchData} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {statCards.map((card) => (
          <Grid key={card.label} size={{ xs: 12, sm: 6, md: 'auto' }} sx={{ flex: 1 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                </Box>
                <Typography variant="h5" fontWeight={700} sx={{ color: card.color, mt: 0.5 }}>
                  {loading ? <CircularProgress size={20} /> : card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Transferências" />
        <Tab label="Corredores" />
        <Tab label="Taxas de Câmbio" />
      </Tabs>

      {/* TAB 0 — Transferências */}
      {tab === 0 && (
        <>
          {/* Filtros */}
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                  <TextField
                    select fullWidth size="small" label="Estado"
                    value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <MenuItem key={k} value={k}>{v.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                  <TextField
                    select fullWidth size="small" label="País Destino"
                    value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {['PT', 'US', 'BR', 'FR', 'NL'].map((c) => (
                      <MenuItem key={c} value={c}>{COUNTRY_FLAGS[c]} {c}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {loading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <CircularProgress size={32} />
                </Box>
              ) : (
                <DataTable
                  columns={columns}
                  data={transfers}
                  loading={false}
                  emptyMessage="Nenhuma remessa encontrada"
                />
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* TAB 1 — Corredores */}
      {tab === 1 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            {loading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>
            ) : corridorStats.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">Nenhum dado de corredor disponível</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Corredor</TableCell>
                    <TableCell align="right">Transações</TableCell>
                    <TableCell align="right">Volume (CVE)</TableCell>
                    <TableCell align="right">Taxas Cobradas</TableCell>
                    <TableCell align="right">Valor Médio</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Peso no Volume</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {corridorStats.map((c, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{COUNTRY_FLAGS['CV'] ?? '🌍'} CV</Typography>
                          <Typography color="text.secondary">→</Typography>
                          <Typography>{COUNTRY_FLAGS[c.dest_country] ?? '🌍'} {c.dest_country}</Typography>
                          <Chip label={c.dest_currency} size="small" variant="outlined" sx={{ ml: 0.5 }} />
                        </Box>
                      </TableCell>
                      <TableCell align="right"><Typography variant="body2" fontWeight={600}>{c.total}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2">{formatCVE(Number(c.volume_cve))}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2" color="success.main">{formatCVE(Number(c.fees_collected))}</Typography></TableCell>
                      <TableCell align="right"><Typography variant="body2">{formatCVE(Number(c.avg_amount))}</Typography></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {Number(c.completed) > 0 && <Chip label={`✓ ${c.completed}`} color="success" size="small" />}
                          {Number(c.processing) > 0 && <Chip label={`⏳ ${c.processing}`} color="warning" size="small" />}
                          {Number(c.failed) > 0 && <Chip label={`✗ ${c.failed}`} color="error" size="small" />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <CorridorProgress value={Number(c.volume_cve)} max={maxVolume} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* TAB 2 — Taxas de Câmbio */}
      {tab === 2 && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              CVE está fixo ao EUR (1 EUR = 110.265 CVE) conforme Acordo de Cooperação Cambial Portugal-Cabo Verde.
            </Alert>
          </Grid>
          {Object.entries(exchangeRates).map(([currency, rate]) => (
            <Grid key={currency} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>CVE / {currency}</Typography>
                    <Chip
                      label={currency === 'EUR' ? 'Fixo' : 'Variável'}
                      color={currency === 'EUR' ? 'success' : 'info'}
                      size="small"
                    />
                  </Box>
                  <Grid container spacing={1}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Compra</Typography>
                      <Typography variant="body1" fontWeight={600} color="success.main">
                        {rate.buy.toFixed(3)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="text.secondary">Venda</Typography>
                      <Typography variant="body1" fontWeight={600} color="error.main">
                        {rate.sell.toFixed(3)}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Spread: {((rate.sell - rate.buy) / rate.buy * 100).toFixed(2)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
